/**
 * The live platform's word to the old boards (build 7).
 *
 * Renders nothing and sits in the shell. Two jobs:
 *
 *   - The moment the engine arrives — inside the loader's own queue, so before
 *     any board draws — it is told whether this session is the live platform
 *     (`setOnPlatform`). On the platform the boards stop drawing sample
 *     figures as if they were the airport's: missing data is ungraded, the
 *     Overview says what it does not know, and the registers below lose their
 *     sample rows. Under the mock nothing changes.
 *   - On the platform, it reads back the last registers that lived in memory:
 *     the assessments (test records), the carousel breakdowns (finished
 *     breakdown work in the register) and the alarm acknowledgements (notes).
 *     The building and landside rounds and the utility checks come back
 *     through InspectionsFeed.
 *
 * Like the other feeds it asks for nothing until the engine is here, never
 * causes it to load, and imports the merge only when there is something to
 * merge. A refused read empties a register to this browser's own entries and
 * the board says why.
 */
import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useSession } from "../../../app/SessionContext";
import { DataSourceError } from "../../../api/dataSource";
import { useWorkOrders } from "../../../api/queries";
import { demoIfLoaded, whenDemoLoaded } from "../../domain/load";
import { ALARM_ACK_TOPIC } from "../writes/systems";

const merger = () => import("../mergeBoards");

const refusedWhy = (e: unknown): string | undefined =>
  e instanceof DataSourceError && e.status === 403 ? e.message : undefined;

export function BoardsFeed() {
  const { source, site } = useSession();
  const live = source.kind === "platform";
  const [engine, setEngine] = useState(() => demoIfLoaded() !== null);

  /* synchronously on arrival: a board must not draw the sample on the live site even once */
  useEffect(() => {
    let gone = false;
    whenDemoLoaded((d) => {
      if (gone) return;
      try {
        d.setOnPlatform(live);
      } catch {
        /* an engine without the switch draws as it always has */
      }
      setEngine(true);
    });
    return () => {
      gone = true;
    };
  }, [live]);

  const on = live && engine;
  const tests = useQuery({
    queryKey: ["test-records", site.id],
    queryFn: () => source.listTestRecords(site.id),
    staleTime: 30_000,
    enabled: on,
    retry: (n, e) => !refusedWhy(e) && n < 2,
  });
  const orders = useWorkOrders();
  const acks = useQuery({
    queryKey: ["notes", site.id, ALARM_ACK_TOPIC],
    queryFn: () => source.listNotes(site.id, ALARM_ACK_TOPIC),
    staleTime: 30_000,
    /* notes announce nothing, so a colleague's acknowledgement is polled for */
    refetchInterval: on ? 30_000 : false,
    enabled: on,
    retry: false,
  });

  const testData = tests.data;
  const testRefused = refusedWhy(tests.error);
  const testFailed = tests.isError && !testRefused ? (tests.error as Error)?.message : undefined;
  useEffect(() => {
    if (!on) return;
    if (testRefused) {
      whenDemoLoaded(() => void merger().then((m) => m.mergeAssessments(null, { state: "refused", note: testRefused })));
      return;
    }
    if (testFailed && !testData) {
      whenDemoLoaded(() => void merger().then((m) => m.mergeAssessments(null, { state: "error", note: testFailed })));
      return;
    }
    if (!testData) return;
    whenDemoLoaded(() => void merger().then((m) => m.mergeAssessments(testData, { state: "ok" })));
  }, [on, testData, testRefused, testFailed]);

  const woItems = orders.data?.items;
  useEffect(() => {
    if (!on || !woItems) return;
    whenDemoLoaded(() => void merger().then((m) => m.mergeCarouselFaults(woItems)));
  }, [on, woItems]);

  const ackData = acks.data;
  useEffect(() => {
    if (!on || !ackData) return;
    whenDemoLoaded(() => void merger().then((m) => m.mergeAlarmAcks(ackData)));
  }, [on, ackData]);

  return null;
}
